<template>
	<div>
		<div
			class="bg-offwhite transition-all duration-500 dark:bg-offmain"
			:class="{
				'p-4': fullscreen && screen.width > 768,
				'stage pb-0': !fullscreen && screen.width >= 768,
				stage: screen.width < 768
			}"
		>
			<div class="flex flex-col gap-4 pb-4 md:gap-6">
				<div class="flex flex-col gap-4 md:gap-2">
					<div class="flex justify-between gap-2 pt-2" :class="{ 'pt-4': fullscreen }">
						<div class="flex max-w-full gap-2">
							<img
								class="h-12 w-12 rounded-full"
								:src="user.picture"
								:alt="user.name + ' profile picture'"
							/>
							<div class="flex flex-col">
								<h2 class="text-lg font-medium xs:text-lg sm:text-xl">
									{{
										user.name.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
											? 'Your dashboard'
											: 'Welcome ' + user.name
									}}
								</h2>
								<p v-if="user.email" class="text-[10px] opacity-75 xs:text-xs">{{ user.email }}</p>
							</div>
						</div>
						<div class="flex items-center justify-center">
							<button
								class="cursor-default rounded-md bg-black bg-opacity-0 p-2 transition-all duration-300 hover:bg-opacity-15 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-opacity-40 xs:p-3 md:p-4"
								aria-label="Refresh All"
								:disabled="loading"
								@click="refreshAll"
							>
								<div :class="{ 'animate-spin': loading }">
									<span class="fa-light fa-sync scale-x-[-1] opacity-50"></span>
								</div>
							</button>
						</div>
					</div>
					<section class="rounded-lg bg-white p-4 shadow-md dark:bg-offmaindark">
						<h3 class="mb-4 text-lg font-semibold">Quick Actions</h3>
						<div class="flex flex-wrap gap-4">
							<router-link
								v-for="action in quickActions"
								v-show="action.show === undefined || action.show"
								:key="action.to"
								:to="action.to"
								:class="['jmiBtn grow md:grow-0', action.bgClass]"
								@click="action.onClick ? action.onClick() : null"
							>
								<span :class="['mr-2', action.icon]"></span>
								{{ action.text }}
							</router-link>
						</div>
					</section>
				</div>
				<div class="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
					<div class="rounded-lg bg-white shadow-md dark:bg-offmaindark">
						<div class="flex flex-row items-center justify-between space-y-0 p-6 pb-2">
							<h3 class="whitespace-nowrap text-sm font-medium tracking-tight">
								Recent International Calls
							</h3>
							<span class="fa-light fa-globe"></span>
						</div>
						<div class="p-6">
							<div class="grid gap-2">
								<div
									v-for="(call, i) in recentInternationalCalls"
									:key="i"
									class="flex flex-col gap-0.5 overflow-hidden border-b border-b-offmain pb-2 last:border-b-0 dark:border-b-offwhite"
								>
									<div class="flex justify-between gap-1 overflow-hidden">
										<p class="truncate">
											{{ call.account_name }}
										</p>
										<p class="truncate whitespace-nowrap text-sm">
											{{ $global.dateTimeFormat(call.start_time) }}
										</p>
									</div>
									<div class="flex flex-row items-center justify-between">
										<div class="w-1/3 truncate text-start text-xs font-medium">
											{{ call.a_call_from }}
										</div>
										<div
											class="flex w-1/3 flex-nowrap items-center justify-center gap-0.5 overflow-hidden truncate text-xs font-bold text-blue-600 dark:text-blue-400"
											@mouseenter="createTooltip($event, call.zone_name)"
										>
											<img
												:src="getCountryFlagEmoji(call.zone_name)"
												:alt="getCountryFlagName(call.zone_name) + ' Flag Icon'"
												class="h-4 w-4"
											/>
											<p class="truncate text-blue-800 dark:text-blue-200">
												{{ call.zone_name }}
											</p>
										</div>
										<div class="w-1/3 truncate text-end text-xs font-medium">
											{{ call.call_to }}
										</div>
									</div>
								</div>
								<div v-if="recentInternationalCallsLoading && !recentInternationalCalls?.length">
									<div
										v-for="i in 5"
										:key="i"
										class="flex flex-col gap-0.5 border-b border-b-offmain pb-2 last:border-b-0 dark:border-b-offwhite"
									>
										<div class="flex justify-between">
											<div class="mt-1.5 h-6 w-28 animate-pulse rounded-md bg-neutral-500"></div>
											<div class="mt-1.5 h-6 w-28 animate-pulse rounded-md bg-neutral-500"></div>
										</div>
										<div class="flex flex-row items-center justify-between">
											<div class="mt-1 h-2 w-12 animate-pulse rounded-md bg-neutral-500"></div>
											<div
												class="flex w-1/3 flex-nowrap items-center justify-center gap-0.5 overflow-hidden truncate text-xs font-bold text-blue-600 dark:text-blue-400"
											>
												<div
													class="mt-1 h-3 w-28 animate-pulse rounded-md bg-neutral-500"
												></div>
											</div>
											<div class="mt-1 h-2 w-12 animate-pulse rounded-md bg-neutral-500"></div>
										</div>
									</div>
								</div>
								<div
									v-if="!recentInternationalCallsLoading && !recentInternationalCalls?.length"
									class="flex h-[284px] w-full items-center justify-center"
								>
									<p class="text-sm text-gray-500 dark:text-gray-400">
										No recent international calls
									</p>
								</div>
							</div>
							<!-- <div class="text-2xl font-bold pt-4">+120</div>
							<p class="text-xs text-gray-500 dark:text-gray-400">+15% from last month</p> -->
						</div>
					</div>
					<div class="rounded-lg bg-white shadow-md dark:bg-offmaindark">
						<div class="flex flex-row items-center justify-between space-y-0 p-6 pb-2">
							<h3 class="whitespace-nowrap text-sm font-medium tracking-tight">Recent Failed Calls</h3>
							<span class="fa-light fa-circle-exclamation"></span>
						</div>
						<div class="p-6">
							<div class="grid gap-2">
								<div
									v-for="(call, i) in recentFailedCalls"
									:key="i"
									class="flex flex-col gap-0.5 overflow-hidden border-b border-b-offmain pb-2 last:border-b-0 dark:border-b-offwhite"
								>
									<div class="flex justify-between gap-1 overflow-hidden">
										<p class="truncate">{{ call.account_name }}</p>
										<p class="whitespace-nowrap text-sm">
											{{ $global.dateTimeFormat(call.start_time) }}
										</p>
									</div>
									<div class="flex flex-row items-center justify-between">
										<div class="w-1/3 truncate text-start text-xs font-medium">
											{{ call.a_call_from }}
										</div>
										<p
											class="w-1/3 cursor-default text-center text-xs font-bold text-red-600 dark:text-red-400"
											@mouseenter="tooltip($event, call.hangup_cause)"
										>
											{{ call.hangup_code }}
										</p>
										<div class="w-1/3 truncate text-end text-xs font-medium">
											{{ call.call_to }}
										</div>
									</div>
								</div>
								<div v-if="recentFailedCallsLoading && !recentFailedCalls?.length">
									<div
										v-for="i in 5"
										:key="i"
										class="flex flex-col gap-0.5 border-b border-b-offmain pb-2 last:border-b-0 dark:border-b-offwhite"
									>
										<div class="flex justify-between">
											<div class="mt-1.5 h-6 w-28 animate-pulse rounded-md bg-neutral-500"></div>
											<div class="mt-1.5 h-6 w-28 animate-pulse rounded-md bg-neutral-500"></div>
										</div>
										<div class="flex flex-row items-center justify-between">
											<div class="mt-1 h-2 w-12 animate-pulse rounded-md bg-neutral-500"></div>
											<div
												class="flex w-1/3 flex-nowrap items-center justify-center gap-0.5 overflow-hidden truncate text-xs font-bold text-blue-600 dark:text-blue-400"
											>
												<div
													class="mt-1 h-3 w-12 animate-pulse rounded-md bg-neutral-500"
												></div>
											</div>
											<div class="mt-1 h-2 w-12 animate-pulse rounded-md bg-neutral-500"></div>
										</div>
									</div>
								</div>
								<div
									v-if="!recentFailedCallsLoading && !recentFailedCalls?.length"
									class="flex h-[284px] w-full items-center justify-center"
								>
									<p class="text-sm text-gray-500 dark:text-gray-400">No recent failed calls</p>
								</div>
							</div>
							<!-- <div class="text-2xl pt-4 font-bold">+45</div>
							<p class="text-xs text-gray-500 dark:text-gray-400">+10% from last month</p> -->
						</div>
					</div>
					<div class="rounded-lg bg-white shadow-md dark:bg-offmaindark">
						<div class="flex flex-row items-center justify-between space-y-0 p-6 pb-2">
							<h3 class="whitespace-nowrap text-sm font-medium tracking-tight">Recent Inbound Calls</h3>
							<span class="fa-light fa-phone"></span>
						</div>
						<div class="p-6">
							<div class="grid gap-2">
								<div
									v-for="(call, i) in recentInboundCalls"
									:key="i"
									class="flex flex-col gap-0.5 overflow-hidden border-b border-b-offmain pb-2 last:border-b-0 dark:border-b-offwhite"
								>
									<div class="flex justify-between gap-1 overflow-hidden">
										<p class="truncate">{{ call.account_name }}</p>
										<p class="whitespace-nowrap text-sm">
											{{ $global.dateTimeFormat(call.a_start_time) }}
										</p>
									</div>
									<div class="flex flex-row items-center justify-between">
										<div class="w-1/3 truncate text-start text-xs font-medium">
											{{ call.a_call_from }}
										</div>
										<p
											class="w-1/3 truncate text-center text-xs font-bold text-green-600 dark:text-green-400"
											@mouseenter="
												createTooltip($event, $global.prettyPrint(call.inbound_app_name))
											"
										>
											{{ $global.prettyPrint(call.inbound_app_name) }}
										</p>
										<div class="w-1/3 truncate text-end text-xs font-medium">
											{{ call.phone_number }}
										</div>
									</div>
								</div>
								<div v-if="recentInboundCallsLoading && !recentInboundCalls?.length">
									<div
										v-for="i in 5"
										:key="i"
										class="flex flex-col gap-0.5 border-b border-b-offmain pb-2 last:border-b-0 dark:border-b-offwhite"
									>
										<div class="flex justify-between">
											<div class="mt-1.5 h-6 w-28 animate-pulse rounded-md bg-neutral-500"></div>
											<div class="mt-1.5 h-6 w-28 animate-pulse rounded-md bg-neutral-500"></div>
										</div>
										<div class="flex flex-row items-center justify-between">
											<div class="mt-1 h-2 w-12 animate-pulse rounded-md bg-neutral-500"></div>
											<div
												class="flex w-1/3 flex-nowrap items-center justify-center gap-0.5 overflow-hidden truncate text-xs font-bold text-blue-600 dark:text-blue-400"
											>
												<div
													class="mt-1 h-3 w-16 animate-pulse rounded-md bg-neutral-500"
												></div>
											</div>
											<div class="mt-1 h-2 w-12 animate-pulse rounded-md bg-neutral-500"></div>
										</div>
									</div>
								</div>
								<div
									v-if="!recentInboundCallsLoading && !recentInboundCalls?.length"
									class="flex h-[284px] w-full items-center justify-center"
								>
									<p class="text-sm text-gray-500 dark:text-gray-400">No recent inbound calls</p>
								</div>
							</div>
							<!-- <div class="text-2xl pt-4 font-bold">+300</div>
							<p class="text-xs text-gray-500 dark:text-gray-400">+25% from last month</p> -->
						</div>
					</div>
					<div class="rounded-lg bg-white shadow-md dark:bg-offmaindark">
						<div class="flex flex-row items-center justify-between space-y-0 p-6 pb-2">
							<h3 class="whitespace-nowrap text-sm font-medium tracking-tight">
								Top Accounts this month
							</h3>
							<span class="fa-light fa-list"></span>
						</div>
						<div class="grid gap-4 p-6">
							<div v-for="(u, i) in topAccounts" :key="i" class="overflow-hidden">
								<div class="flex flex-row items-center justify-between overflow-hidden">
									<div class="flex w-full gap-1 overflow-hidden text-sm font-medium">
										<p class="truncate">{{ u.account_name }}</p>
										<a
											v-if="u.account_id"
											class="group flex cursor-pointer gap-1"
											:aria-label="`View ${u.account_name} Link`"
											:href="`/accounts/${u.account_id}`"
										>
											<svg
												class="mt-1"
												width="14"
												height="14"
												viewBox="0 0 24 24"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path d="M20 5H8V9H6V3H22V21H6V15H8V19H20V5Z" fill="currentColor" />
												<path
													d="M13.0743 16.9498L11.6601 15.5356L14.1957 13H2V11H14.1956L11.6601 8.46451L13.0743 7.05029L18.024 12L13.0743 16.9498Z"
													fill="currentColor"
												/>
											</svg>
										</a>
									</div>
									<div class="ml-1 text-sm font-medium">{{ u.total }}</div>
								</div>
								<div>
									<div
										class="inline-block h-3 py-2"
										:style="{
											width: `${
												(parseInt(u.total_inbound) / parseInt(topAccounts[0]?.total)) * 100
											}%`
										}"
										@mouseenter="tooltip($event, `Inbound: ${u.total_inbound}`)"
									>
										<div class="dark:bg-primary-dark h-1 bg-primary"></div>
									</div>
									<div
										class="inline-block h-3 py-2"
										:style="{
											width: `${
												(parseInt(u.total_outbound) / parseInt(topAccounts[0]?.total)) * 100
											}%`
										}"
										@mouseenter="tooltip($event, `Outbound: ${u.total_outbound}`)"
									>
										<div class="dark:bg-primary-dark h-1 bg-blue-500"></div>
									</div>
									<div
										class="inline-block h-3 py-2"
										:style="{
											width: `${
												((parseInt(topAccounts[0]?.total) - parseInt(u.total)) /
													parseInt(topAccounts[0]?.total)) *
												100
											}%`
										}"
									>
										<div class="h-1 bg-gray-200 dark:bg-gray-700"></div>
									</div>
								</div>
							</div>
							<div v-if="topAccountsLoading && !topAccounts?.length" class="grid gap-1.5">
								<div v-for="i in 5" :key="i" class="flex flex-col gap-0.5 pb-2">
									<div class="flex justify-between">
										<div class="mt-1.5 h-6 w-28 animate-pulse rounded-md bg-neutral-500"></div>
										<div class="mt-1.5 h-2 w-8 animate-pulse rounded-md bg-neutral-500"></div>
									</div>
									<div class="flex flex-row items-center justify-between">
										<div class="mt-1 h-2 w-full animate-pulse rounded-md bg-neutral-500"></div>
									</div>
								</div>
							</div>
							<div
								v-if="!topAccountsLoading && !topAccounts?.length"
								class="flex h-[284px] w-full items-center justify-center"
							>
								<p class="text-sm text-gray-500 dark:text-gray-400">No accounts available</p>
							</div>
						</div>
					</div>
				</div>
				<div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					<div class="rounded-lg bg-white shadow-md dark:bg-offmaindark">
						<div class="flex flex-row items-center justify-between space-y-0 p-6 pb-2">
							<h3 class="whitespace-nowrap text-sm font-medium tracking-tight">
								Calls by Hangup Code this month
							</h3>
							<span class="fa-light fa-user"></span>
						</div>
						<div class="relative mx-auto h-[300px] max-w-[90vw] p-4 sm:max-w-[60vw] md:h-[400px]">
							<bar-chart
								:data="callsPerHangupCode"
								:chart-options="barChartOptions"
								:chart-id="'calls-per-hangup-' + Date.now()"
							/>
							<div v-if="initialLoading" class="flex h-full w-full items-center justify-center">
								<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
									<div class="row margin-b20 center-xs center-md center-lg">
										<div class="loader"></div>
									</div>
								</div>
							</div>
							<div
								v-if="!initialLoading && !callsPerHangupCode?.datasets[0]?.data?.length"
								class="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
							>
								<p class="text-sm text-gray-500 dark:text-gray-400">No calls available</p>
							</div>
						</div>
					</div>
					<div class="rounded-lg bg-white shadow-md dark:bg-offmaindark">
						<div class="flex flex-row items-center justify-between space-y-0 p-6 pb-2">
							<h3 class="whitespace-nowrap text-sm font-medium tracking-tight">
								Calls per Carrier this month
							</h3>
							<span class="fa-light fa-satellite"></span>
						</div>
						<div class="relative mx-auto h-[300px] max-w-[90vw] p-4 sm:max-w-[60vw] md:h-[400px]">
							<pie-chart
								:data="callsPerCarrier"
								:chart-options="globalChartOptions"
								:chart-id="'calls-per-carrier-' + Date.now()"
							/>
							<div v-if="initialLoading" class="flex h-full w-full items-center justify-center">
								<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
									<div class="row margin-b20 center-xs center-md center-lg">
										<div class="loader"></div>
									</div>
								</div>
							</div>
							<div
								v-if="!initialLoading && !callsPerCarrier?.datasets[0]?.data?.length"
								class="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
							>
								<p class="text-sm text-gray-500 dark:text-gray-400">No calls available</p>
							</div>
						</div>
					</div>
					<div
						class="relative col-span-1 rounded-lg bg-white shadow-md dark:bg-offmaindark md:col-span-2 lg:col-span-1"
					>
						<div class="flex flex-row items-center justify-between space-y-0 p-6 pb-2">
							<h3 class="whitespace-nowrap text-sm font-medium tracking-tight">
								Calls per Account by day
							</h3>
							<span class="fa-light fa-credit-card"></span>
						</div>
						<div class="relative mx-auto h-[300px] max-w-[90vw] p-4 sm:max-w-[60vw] md:h-[400px]">
							<line-chart
								:data="callsPerAccount"
								:chart-options="lineChartOptions"
								:chart-id="'calls-per-account-' + Date.now()"
							/>
							<div v-if="initialLoading" class="flex h-full w-full items-center justify-center">
								<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
									<div class="row margin-b20 center-xs center-md center-lg">
										<div class="loader"></div>
									</div>
								</div>
							</div>
							<div
								v-if="!initialLoading && !callsPerAccount?.datasets[0]?.data?.length"
								class="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
							>
								<p class="text-sm text-gray-500 dark:text-gray-400">No calls available</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import countryFlagEmojis from '@/assets/country-flag-emojis.json'
import moment from 'moment'
import cdrsApi from '../scripts/calldetailrecords/calldetailrecords.api'

export default {
	inject: ['user', 'userRoles', 'tenants', 'userAccounts', 'fullscreen'],
	data() {
		return {
			moment,
			globalChartOptions: {
				responsive: true,
				maintainAspectRatio: false
			},
			barChartOptions: {
				plugins: {
					legend: {
						display: false
					},
					title: {
						display: false
					}
				},
				responsive: true,
				maintainAspectRatio: false
			},
			lineChartOptions: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					y: {
						beginAtZero: true
					}
				}
			},
			callsPerHangupCode: {
				labels: [],
				datasets: [
					{
						backgroundColor: ['#419e5a', '#e0a458', '#dd4346', '#6478c7', '#7fcece', '#424a59'],
						data: []
					}
				]
			},
			callsPerCarrier: {
				labels: [],
				datasets: [
					{
						backgroundColor: ['#6478c7', '#e0a458', '#419e5a', '#dd4346', '#7fcece', '#424a59'],
						borderColor: ['#6478c7', '#e0a458', '#419e5a', '#dd4346', '#7fcece', '#424a59'],
						data: []
					}
				]
			},
			callsPerAccount: {
				availableColours: ['#419e5a', '#e0a458', '#dd4346', '#6478c7', '#7fcece'],
				labels: [
					moment().subtract(6, 'days').format('DD/MM'),
					moment().subtract(5, 'days').format('DD/MM'),
					moment().subtract(4, 'days').format('DD/MM'),
					moment().subtract(3, 'days').format('DD/MM'),
					moment().subtract(2, 'days').format('DD/MM'),
					moment().subtract(1, 'days').format('DD/MM'),
					moment().format('DD/MM')
				],
				datasets: []
			},
			recentInternationalCalls: [],
			recentFailedCalls: [],
			recentInboundCalls: [],
			topAccounts: [],
			topCarriers: [],
			topHangupCodes: [],
			callsByDayPerAccount: [],
			interval: null,
			initialLoading: true,
			loading: false,
			screen: {
				width: 0,
				height: 0
			},
			recentInternationalCallsLoading: true,
			recentFailedCallsLoading: true,
			recentInboundCallsLoading: true,
			topAccountsLoading: true,
			topCarriersLoading: true,
			topHangupCodesLoading: true,
			callsByDayPerAccountLoading: true
		}
	},

	computed: {
		screenSize() {
			return {
				width: window.innerWidth,
				height: window.innerHeight
			}
		},
		isIOS() {
			return (
				['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
					navigator.platform
				) ||
				(navigator.userAgent.includes('Mac') && 'ontouchend' in document)
			)
		},
		quickActions() {
			return [
				{
					to: '/phonenumbers/new',
					text: 'New Phone Number',
					icon: 'fa-light fa-phone-incoming',
					bgClass: 'blue'
				},
				{
					to: '/calls',
					text: 'Search Calls',
					icon: 'fa-light fa-magnifying-glass',
					bgClass: 'green'
				},
				{
					to: '/reports',
					text: 'View Reports',
					icon: 'fa-light fa-pie-chart',
					bgClass: 'orange'
				}
			]
		}
	},

	watch: {},

	mounted() {
		this.refreshAll()
		this.updateScreenSize()

		window.addEventListener('resize', this.updateScreenSize)

		this.interval = setInterval(() => {
			this.refreshAll()
		}, 30000)

		window?.navigation?.addEventListener('navigate', () => {
			this.fullscreen = false
		})
	},

	beforeUnmount() {
		clearInterval(this.interval)
	},

	methods: {
		updateScreenSize() {
			this.screen = { width: window.innerWidth, height: window.innerHeight }
		},
		async refreshAll() {
			try {
				this.loading = true

				let recentInternationalCallsReq = cdrsApi.getRecentInternationalCalls()
				let recentFailedCallsReq = cdrsApi.getRecentFailedCalls()
				let recentInboundCallsReq = cdrsApi.getRecentInboundCalls()
				let topAccountsReq = cdrsApi.getTopAccounts()
				let topCarriersReq = cdrsApi.getTopCarriers()
				let topHangupCodesReq = cdrsApi.getTopHangupCodes()
				let callsByDayPerAccountReq = cdrsApi.getCallsByDayPerAccount()

				this.recentInternationalCalls = (await recentInternationalCallsReq) || []
				this.recentInternationalCallsLoading = false
				this.recentFailedCalls = (await recentFailedCallsReq) || []
				this.recentFailedCallsLoading = false
				this.recentInboundCalls = (await recentInboundCallsReq) || []
				this.recentInboundCallsLoading = false
				this.topAccounts = (await topAccountsReq) || []
				this.topAccountsLoading = false
				this.topCarriers = (await topCarriersReq) || []
				this.topCarriersLoading = false
				this.topHangupCodes = (await topHangupCodesReq) || []
				this.topHangupCodesLoading = false
				this.callsByDayPerAccount = (await callsByDayPerAccountReq) || []
				this.callsByDayPerAccountLoading = false

				this.callsPerCarrier.labels = this.topCarriers.map((c) => c.carrier_name)
				this.callsPerCarrier.datasets[0].data = this.topCarriers.map((c) => parseInt(c.total) || 0)

				this.callsPerHangupCode.labels = this.topHangupCodes.map((c) => c.hangup_code)
				this.callsPerHangupCode.datasets[0].data = this.topHangupCodes.map((c) => parseInt(c.total) || 0)

				let availableColours = JSON.parse(JSON.stringify(this.callsPerAccount.availableColours))

				this.callsPerAccount.datasets = this.callsByDayPerAccount.map((c) => {
					let colour = availableColours.shift()
					return {
						label: c.account_name,
						backgroundColor: colour,
						borderColor: colour,
						data: [
							parseInt(c.date_6),
							parseInt(c.date_5),
							parseInt(c.date_4),
							parseInt(c.date_3),
							parseInt(c.date_2),
							parseInt(c.date_1),
							parseInt(c.date_0)
						]
					}
				})

				this.loading = false
				this.initialLoading = false
			} catch (error) {
				this.loading = false
				console.error('Refreshing dashboard', error)
			}
		},
		getCountryFlagEmoji(countryCode) {
			if (countryCode)
				return (
					countryFlagEmojis.find((c) => countryCode.toLowerCase().indexOf(c.name.toLowerCase()) > -1)
						?.image || ''
				)
			return ''
		},
		getCountryFlagName(countryCode) {
			if (countryCode)
				return (
					countryFlagEmojis.find((c) => countryCode.toLowerCase().indexOf(c.name.toLowerCase()) > -1)?.name ||
					''
				)
			return ''
		}
	}
}
</script>
